/**
 * Flux Agent page: an energy-aware AI coach that converses with the user, asks probing questions,
 * and rearranges their schedule in the background through tool calls. Loads the agent status and
 * conversation list, keeps per-conversation message threads, sends typed, dictated, or voice-mode
 * messages with attached files, renders the agent's actions under each reply, and refreshes the
 * planner stores when a reply completed a schedule move. Memory review is available to everyone;
 * the trainer console and training-mode conversations appear only when `status.trainer` is true.
 *
 * @module apps/web/src/pages/FluxAgentPage
 */

import { useCallback, useEffect, useRef, useState, type DragEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AudioLines,
  Bot,
  Brain,
  Clock,
  GraduationCap,
  Loader2,
  MessagesSquare,
  RefreshCw,
  Settings2,
  Sparkles,
  Upload,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { afterNextPaint } from '@/lib/afterPaint';
import { FluxAgentActionCards } from '@/components/flux-agent/FluxAgentActionCards';
import { FluxAgentCoachPanel } from '@/components/flux-agent/FluxAgentCoachPanel';
import { FluxAgentComposer } from '@/components/flux-agent/FluxAgentComposer';
import { FluxAgentPhotoThumb } from '@/components/flux-agent/FluxAgentMediaViews';
import { useFluxAgentPhotos } from '@/components/flux-agent/useFluxAgentPhotos';
import { FluxAgentConversationList } from '@/components/flux-agent/FluxAgentConversationList';
import { FluxAgentMarkdown } from '@/components/flux-agent/FluxAgentMarkdown';
import { FluxAgentScheduled } from '@/components/flux-agent/FluxAgentScheduled';
import { FluxViewsDialog } from '@/components/flux-agent/views/FluxViewsDialog';
import { FluxAgentMemoryDialog } from '@/components/flux-agent/FluxAgentMemoryDialog';
import { FluxAgentTrainerConsole } from '@/components/flux-agent/FluxAgentTrainerConsole';
import { FluxAgentSuggestionCards } from '@/components/flux-agent/learning/FluxAgentSuggestionCards';
import { useDataRequestDeepLink } from '@/components/flux-agent/learning/useDataRequestDeepLink';
import { useConversationEndSignal } from '@/components/flux-agent/learning/useConversationEndSignal';
import { useFluxAgentAttachments } from '@/components/flux-agent/useFluxAgentAttachments';
import { useFluxAgentVoiceMode } from '@/components/flux-agent/useFluxAgentVoice';
import { useFluxAgentLiveVoice } from '@/components/flux-agent/useFluxAgentLiveVoice';
import { FluxAgentLivePanel, LIVE_LABELS } from '@/components/flux-agent/FluxAgentLivePanel';
import {
  FluxAgentVoiceDebug,
  FluxAgentVoicePanel,
  VOICE_LABELS,
} from '@/components/flux-agent/FluxAgentVoicePanel';
import { refreshAfterAgentActions } from '@/components/flux-agent/plannerRefresh';
import {
  fluxAgentApi,
  hasCompletedScheduleAction,
  withResolvedCard,
  type FluxAgentAction,
  type FluxAgentConversation,
  type FluxAgentLiveForm,
  type FluxAgentMessage,
  type FluxAgentMode,
  type FluxAgentStatus,
} from '@/lib/fluxAgentApi';
import { markdownToSpeech } from '@/lib/fluxAgentMarkdown';
import { canRunVoiceEngine } from '@/lib/fluxAgentVoiceEngine';
import { canRunLiveVoice } from '@/lib/fluxAgentLive';
import { cn } from '@/lib/utils';

/** Example prompts shown in an empty conversation; tapping one fills the composer draft. */
const STARTERS = [
  'I just finished a pile of chores and I’m wiped out.',
  'I really don’t feel like doing my errands today.',
  'Help me plan tomorrow around my energy.',
];

/** Reply text and actions received so far from a streamed turn. */
interface StreamingReply {
  /** Conversation the reply belongs to. */
  conversationId: string;
  /** Answer text so far; replaced when a new model round starts. */
  text: string;
  /** Model round that produced `text`. */
  round: number;
  /** Tool actions completed so far. */
  actions: FluxAgentAction[];
  /** Create and edit forms Flux Agent is filling in right now, by tool-call slot. */
  forms: FluxAgentLiveForm[];
}

/** Fold one streamed text fragment into the provisional reply of conversation `id`. */
function withDelta(
  current: StreamingReply | null,
  id: string,
  delta: string,
  round: number
): StreamingReply {
  return current && current.conversationId === id
    ? { ...current, text: round === current.round ? current.text + delta : delta, round }
    : { conversationId: id, text: delta, round, actions: [], forms: [] };
}

/** Add one completed tool action to the provisional reply of conversation `id`. */
function withAction(
  current: StreamingReply | null,
  id: string,
  action: FluxAgentAction
): StreamingReply {
  return current && current.conversationId === id
    ? { ...current, actions: [...current.actions, action] }
    : { conversationId: id, text: '', round: 0, actions: [action], forms: [] };
}

/** Put the latest state of one live form into the provisional reply of conversation `id`. */
function withForm(
  current: StreamingReply | null,
  id: string,
  live: FluxAgentLiveForm
): StreamingReply {
  const base =
    current && current.conversationId === id
      ? current
      : { conversationId: id, text: '', round: 0, actions: [], forms: [] };
  return {
    ...base,
    forms: [...base.forms.filter(form => form.slot !== live.slot), live],
  };
}

/** Messages of a thread up to and including its last user message (drops the reply after it). */
function throughLastUser(thread: FluxAgentMessage[]): FluxAgentMessage[] {
  const last = thread.map(message => message.role).lastIndexOf('user');
  return last >= 0 ? thread.slice(0, last + 1) : thread;
}

/** Optional integration hooks for {@link FluxAgentPage}. */
export interface FluxAgentPageProps {
  /**
   * Resolve the conversation a deep link should open, e.g. one created for `?request=<id>`.
   * Called once on mount with the page's search parameters; return a conversation id to make it
   * active (it is added to the list if missing), or `null` to keep the default selection. Voice
   * deep links (`?voice=1`) still need the user's tap before the microphone starts.
   */
  resolveDeepLinkConversation?: (params: URLSearchParams) => Promise<string | null> | string | null;
}

/**
 * Sort comparator putting the most recently updated conversation first. Compares the server's
 * fixed-width UTC timestamp strings lexically.
 */
function byUpdatedDesc(a: FluxAgentConversation, b: FluxAgentConversation): number {
  return b.updatedAt.localeCompare(a.updatedAt);
}

/** Render the Flux Agent coaching workspace. */
export function FluxAgentPage({ resolveDeepLinkConversation }: FluxAgentPageProps = {}) {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [voiceReady, setVoiceReady] = useState(() => searchParams.get('voice') === '1');
  const voiceDebug = searchParams.get('voiceDebug') === '1';
  const [streaming, setStreaming] = useState<StreamingReply | null>(null);
  const [status, setStatus] = useState<FluxAgentStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [conversations, setConversations] = useState<FluxAgentConversation[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [threads, setThreads] = useState<Record<string, FluxAgentMessage[]>>({});
  const [loadingThreadId, setLoadingThreadId] = useState<string | null>(null);
  const [threadError, setThreadError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  // Mode of the conversation being created, so its empty state shows the right copy at once.
  const [creatingMode, setCreatingMode] = useState<FluxAgentMode>('coach');
  // The in-flight creation, shared by every caller until the server replies.
  const creation = useRef<Promise<FluxAgentConversation | null> | null>(null);
  // Whether the conversation list has loaded once; later refreshes do not show it as loading.
  const listLoaded = useRef(false);
  const [draft, setDraft] = useState('');
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [pendingSince, setPendingSince] = useState(0);
  const [sendError, setSendError] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [showList, setShowList] = useState(false);
  const [memoryOpen, setMemoryOpen] = useState(false);
  const [scheduledOpen, setScheduledOpen] = useState(() => searchParams.get('scheduled') === '1');
  useEffect(() => {
    if (searchParams.get('scheduled') === '1') setScheduledOpen(true);
  }, [searchParams]);
  const scheduledVisibility = (open: boolean) => {
    setScheduledOpen(open);
    if (!open && searchParams.has('scheduled')) {
      const next = new URLSearchParams(searchParams);
      next.delete('scheduled');
      setSearchParams(next, { replace: true });
    }
  };
  const [viewsOpen, setViewsOpen] = useState(false);
  const [trainerOpen, setTrainerOpen] = useState(false);
  const [coachOpen, setCoachOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [learningTick, setLearningTick] = useState(0);
  const threadEnd = useRef<HTMLDivElement>(null);
  const activeIdRef = useRef(activeId);
  activeIdRef.current = activeId;
  const pendingIdRef = useRef(pendingId);
  pendingIdRef.current = pendingId;

  const attachments = useFluxAgentAttachments(status?.limits.maxFileBytes ?? 0);
  const photos = useFluxAgentPhotos(status?.photos?.maxPhotosPerMessage ?? 4);
  const activeConversation = conversations.find(item => item.id === activeId) || null;
  // Mode whose empty state shows: the open conversation's, or the one being created.
  const shownMode = activeConversation?.mode ?? (creating ? creatingMode : undefined);
  const messages = (activeId && threads[activeId]) || [];
  const threadLoading = !!activeId && loadingThreadId === activeId;

  const loadAll = useCallback(async () => {
    setStatusError(null);
    // A refresh (for example on returning to the page) keeps showing the known list.
    setListLoading(!listLoaded.current);
    const [statusResult, listResult] = await Promise.all([
      fluxAgentApi.status(),
      fluxAgentApi.listConversations(),
    ]);
    if (statusResult.ok) setStatus(statusResult.data);
    else setStatusError(statusResult.message);
    if (listResult.ok) {
      listLoaded.current = true;
      const sorted = [...listResult.data.conversations].sort(byUpdatedDesc);
      setConversations(sorted);
      // Keep the empty new conversation if one is being created.
      setActiveId(current => current ?? (creation.current ? null : (sorted[0]?.id ?? null)));
    }
    setListLoading(false);
  }, []);

  // Refresh after the screen has painted, so a mount or a keep-alive reveal shows at once.
  useEffect(() => afterNextPaint(() => void loadAll()), [loadAll]);

  // Integrator hook: open the conversation a deep link points at (e.g. a data request).
  const deepLinkHandled = useRef<string | null>(null);
  useEffect(() => {
    if (
      (!resolveDeepLinkConversation && !searchParams.get('conversation')) ||
      deepLinkHandled.current === searchParams.toString()
    )
      return;
    deepLinkHandled.current = searchParams.toString();
    void (async () => {
      const id =
        searchParams.get('conversation') ||
        (await resolveDeepLinkConversation?.(new URLSearchParams(searchParams)));
      if (!id) return;
      const result = await fluxAgentApi.getConversation(id);
      if (!result.ok) return;
      setConversations(current => [
        result.data.conversation,
        ...current.filter(item => item.id !== id),
      ]);
      setThreads(current => ({ ...current, [id]: result.data.messages }));
      setActiveId(id);
    })();
  }, [resolveDeepLinkConversation, searchParams]);

  const openConversation = useCallback(async (id: string) => {
    setActiveId(id);
    setShowList(false);
    setThreadError(null);
    setSendError(null);
    setLoadingThreadId(id);
    const result = await fluxAgentApi.getConversation(id);
    setLoadingThreadId(current => (current === id ? null : current));
    if (activeIdRef.current !== id) return;
    if (!result.ok) {
      setThreadError(result.message);
      return;
    }
    // A reply in flight will append the stored messages itself; don't drop its optimistic bubble.
    if (pendingIdRef.current !== id) {
      setThreads(current => ({ ...current, [id]: result.data.messages }));
    }
    setConversations(current =>
      current.some(item => item.id === id)
        ? current.map(item => (item.id === id ? result.data.conversation : item))
        : [result.data.conversation, ...current]
    );
  }, []);

  // Load the thread for whichever conversation became active without one cached.
  useEffect(() => {
    if (activeId && !threads[activeId]) void openConversation(activeId);
  }, [activeId, threads, openConversation]);

  useEffect(() => {
    threadEnd.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages.length, pendingId, activeId, streaming?.text.length]);

  // Elapsed-time hint for long tool-running replies.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!pendingId) return;
    const timer = window.setInterval(() => setTick(tick => tick + 1), 1000);
    return () => window.clearInterval(timer);
  }, [pendingId]);

  /**
   * Start a conversation. The empty conversation (starters and composer) shows at once while the
   * server creates it; a second call during creation, such as sending a starter, waits for the same
   * creation instead of starting another. The new conversation opens only if the person has not
   * opened another one meanwhile. A failed creation reopens the previous conversation.
   */
  const createConversation = useCallback(
    (mode: FluxAgentMode = 'coach'): Promise<FluxAgentConversation | null> => {
      if (creation.current) return creation.current;
      const previousId = activeIdRef.current;
      setCreating(true);
      setCreatingMode(mode);
      setActiveId(null);
      setShowList(false);
      setThreadError(null);
      setSendError(null);
      const pending = (async () => {
        const result = await fluxAgentApi.createConversation(mode);
        if (!result.ok) {
          if (activeIdRef.current === null) setActiveId(previousId);
          toast({
            title: 'Couldn’t start a conversation',
            description: result.message,
            variant: 'destructive',
          });
          return null;
        }
        const conversation = result.data.conversation;
        setConversations(current => [
          conversation,
          ...current.filter(item => item.id !== conversation.id),
        ]);
        setThreads(current => ({ ...current, [conversation.id]: [] }));
        if (activeIdRef.current === null) {
          activeIdRef.current = conversation.id;
          setActiveId(conversation.id);
        }
        return conversation;
      })().finally(() => {
        creation.current = null;
        setCreating(false);
      });
      creation.current = pending;
      return pending;
    },
    [toast]
  );

  const deleteConversation = async (conversation: FluxAgentConversation) => {
    if (
      !window.confirm(
        `Delete “${conversation.title || 'this conversation'}”? This can’t be undone.`
      )
    )
      return;
    const result = await fluxAgentApi.deleteConversation(conversation.id);
    if (!result.ok) {
      toast({
        title: 'Couldn’t delete the conversation',
        description: result.message,
        variant: 'destructive',
      });
      return;
    }
    setConversations(current => current.filter(item => item.id !== conversation.id));
    setThreads(current => {
      const next = { ...current };
      delete next[conversation.id];
      return next;
    });
    if (activeIdRef.current === conversation.id) setActiveId(null);
  };

  /**
   * Send text plus ready attachments. Shows the user's message immediately, streams the reply
   * into a provisional bubble (calling `onDelta` for each fragment), then swaps in the stored
   * user message and assistant reply. Resolves with the reply, or null after a failure.
   */
  const sendText = useCallback(
    async (
      text: string,
      onDelta?: (delta: string, round: number) => void,
      prosody?: unknown
    ): Promise<FluxAgentMessage | null> => {
      const fileIds = attachments.readyIds;
      const mediaIds = photos.readyIds;
      const conversation =
        conversations.find(item => item.id === activeIdRef.current) ||
        (await createConversation('coach'));
      if (!conversation) return null;
      const id = conversation.id;
      const optimistic: FluxAgentMessage = {
        id: `pending-${Date.now()}`,
        role: 'user',
        content: text,
        createdAt: new Date().toISOString(),
        actions: [],
        files: [
          ...attachments.attachments.flatMap(item =>
            item.status === 'ready' && item.file ? [{ id: item.file.id, name: item.file.name }] : []
          ),
          ...photos.photos.flatMap(item =>
            item.status === 'ready' && item.media
              ? [{ id: item.media.id, name: 'Photo', kind: 'photo' as const, url: item.previewUrl }]
              : []
          ),
        ],
      };
      setSendError(null);
      setPendingId(id);
      setPendingSince(Date.now());
      setStreaming(null);
      setThreads(current => ({ ...current, [id]: [...(current[id] || []), optimistic] }));

      const result = await fluxAgentApi.streamMessage(id, text, fileIds, {
        prosody,
        mediaIds,
        onDelta: (delta, round) => {
          setStreaming(current => withDelta(current, id, delta, round));
          onDelta?.(delta, round);
        },
        onAction: action => setStreaming(current => withAction(current, id, action)),
        onForm: live => setStreaming(current => withForm(current, id, live)),
      });
      setPendingId(null);
      setStreaming(null);
      if (!result.ok) {
        if (result.code === 'STREAM_INTERRUPTED' || (result.streamed && result.status === 200)) {
          // The server stored the message (and may still store the reply); show what it has.
          setSendError(result.message);
          void openConversation(id);
          return null;
        }
        setThreads(current => ({
          ...current,
          [id]: (current[id] || []).filter(message => message.id !== optimistic.id),
        }));
        setSendError(result.message);
        return null;
      }

      attachments.clear();
      photos.clear();
      setLearningTick(tick => tick + 1);
      const stored = result.data.messages;
      setThreads(current => ({
        ...current,
        [id]: [...(current[id] || []).filter(message => message.id !== optimistic.id), ...stored],
      }));
      setConversations(current => [
        result.data.conversation,
        ...current.filter(item => item.id !== id),
      ]);
      const reply = [...stored].reverse().find(message => message.role === 'assistant') || null;
      if (reply) setAnnouncement(`Flux Agent: ${markdownToSpeech(reply.content).slice(0, 300)}`);
      void refreshAfterAgentActions(stored.flatMap(message => message.actions || []));
      if (hasCompletedScheduleAction(stored)) {
        toast({
          title: 'Schedule updated',
          description: 'Flux Agent moved things around for you.',
        });
      }
      return reply;
    },
    [attachments, photos, conversations, createConversation, openConversation, toast]
  );

  /**
   * After coaching changes were accepted, re-run the active conversation's last message with the
   * updated system prompt and playbooks: the old reply is removed, the new one streams into the
   * provisional bubble, then the stored rows replace it. Failures reload the conversation.
   */
  const rerunLast = useCallback(async () => {
    const id = activeIdRef.current;
    if (!id) return;
    setSendError(null);
    setPendingId(id);
    setPendingSince(Date.now());
    setStreaming(null);
    setThreads(current => ({ ...current, [id]: throughLastUser(current[id] || []) }));
    const result = await fluxAgentApi.rerunLastMessage(id, {
      onDelta: (delta, round) => setStreaming(current => withDelta(current, id, delta, round)),
      onAction: action => setStreaming(current => withAction(current, id, action)),
      onForm: live => setStreaming(current => withForm(current, id, live)),
    });
    setPendingId(null);
    setStreaming(null);
    if (!result.ok) {
      setSendError(result.message);
      void openConversation(id);
      return;
    }
    const stored = result.data.messages;
    setThreads(current => {
      const kept = throughLastUser(current[id] || []);
      return { ...current, [id]: [...kept.slice(0, -1), ...stored] };
    });
    setConversations(current => [
      result.data.conversation,
      ...current.filter(item => item.id !== id),
    ]);
    const reply = [...stored].reverse().find(message => message.role === 'assistant');
    if (reply) setAnnouncement(`Flux Agent: ${markdownToSpeech(reply.content).slice(0, 300)}`);
    toast({
      title: 'Coaching applied',
      description: 'Flux Agent answered your last message again with the updated playbooks.',
    });
  }, [openConversation, toast]);

  // The coaching panel belongs to one conversation; switching conversations closes it.
  useEffect(() => setCoachOpen(false), [activeId]);

  const canCoach =
    !!status?.trainer &&
    activeConversation?.mode === 'coach' &&
    messages.some(message => message.role === 'assistant');

  const submitDraft = async () => {
    const text = draft.trim();
    if (!text && !attachments.readyIds.length && !photos.readyIds.length) return;
    setDraft('');
    const reply = await sendText(text);
    // Keep the typed text on failure unless the user already started a new draft.
    if (!reply) setDraft(current => current || text);
  };

  useDataRequestDeepLink(conversation => {
    setConversations(current => [
      conversation,
      ...current.filter(item => item.id !== conversation.id),
    ]);
    void openConversation(conversation.id);
  });

  const voice = useFluxAgentVoiceMode({
    maxAudioBytes: status?.limits.maxAudioBytes ?? 0,
    serverSpeech: status?.voice.synthesize ?? false,
    send: async (text, onDelta, prosody) =>
      (await sendText(text, onDelta, prosody))?.content ?? null,
    debug: voiceDebug,
  });
  const endConversation = useConversationEndSignal(activeIdRef);
  const stopVoice = () => {
    voice.stop();
    endConversation();
  };
  const voiceActive = voice.phase !== 'off';
  /** The transcribe → chat → speak pipeline; also the fallback when live voice cannot start. */
  const startPipelineVoice = () => {
    setVoiceReady(false);
    voice.start();
  };
  // GPT-Live voice when the server offers it. It talks in the open conversation (including one a
  // `?request=` deep link opened) or a new one, posts the end signal itself after the session
  // closes, and falls back to the pipeline when WebRTC, the microphone or the route is missing.
  const live = useFluxAgentLiveVoice({
    available: !!status?.voice.live,
    ensureConversation: async () =>
      activeIdRef.current ?? (await createConversation('coach'))?.id ?? null,
    onFallback: startPipelineVoice,
    onClosed: id => void openConversation(id),
  });
  const liveMode = status?.enabled !== false && !!status?.voice.live && canRunLiveVoice();
  /** Start voice mode from a tap: GPT-Live when available, otherwise the pipeline. */
  const startVoice = () => {
    setVoiceReady(false);
    if (liveMode) void live.start();
    else voice.start();
  };
  const anyVoiceActive = voiceActive || live.active;

  const onDragOver = (event: DragEvent) => {
    if (!event.dataTransfer.types.includes('Files')) return;
    event.preventDefault();
    setDragging(true);
  };
  const onDrop = (event: DragEvent) => {
    if (!event.dataTransfer.files.length) return;
    event.preventDefault();
    setDragging(false);
    attachments.addFiles(Array.from(event.dataTransfer.files));
  };

  const pendingSeconds = pendingId ? Math.floor((Date.now() - pendingSince) / 1000) : 0;
  const enabled = status?.enabled !== false;
  const canVoice = (enabled && !!status?.voice.transcribe && canRunVoiceEngine()) || liveMode;

  return (
    <div className="mx-auto flex h-full min-h-[30rem] w-full max-w-6xl flex-col gap-3">
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {announcement}
      </div>
      <div className="sr-only" aria-live="assertive">
        {live.active ? LIVE_LABELS[live.phase] : VOICE_LABELS[voice.phase]}
      </div>

      <header className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-3">
          <span className="bg-primary/15 text-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-xl">
            <Sparkles className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h1 className="truncate text-xl font-semibold tracking-tight sm:text-2xl">
              Flux Agent
            </h1>
            <p className="text-muted-foreground hidden text-sm sm:block">
              Talk it through. Flux Agent adjusts your plan around your energy.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5 md:hidden"
            onClick={() => setShowList(value => !value)}
            aria-expanded={showList}
          >
            <MessagesSquare className="h-4 w-4" /> Chats
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => setMemoryOpen(true)}
          >
            <Brain className="h-4 w-4" /> <span className="hidden sm:inline">What it knows</span>
            <span className="sm:hidden">Memory</span>
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => setScheduledOpen(true)}
          >
            <Clock className="h-4 w-4" />
            Scheduled
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => setViewsOpen(true)}>
            Views
          </Button>
          {status?.trainer && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => setTrainerOpen(true)}
            >
              <Settings2 className="h-4 w-4" />{' '}
              <span className="hidden sm:inline">Trainer console</span>
              <span className="sm:hidden">Trainer</span>
            </Button>
          )}
          {canVoice && (
            <Button
              type="button"
              size="sm"
              variant={anyVoiceActive ? 'default' : 'outline'}
              className="gap-1.5"
              onClick={() =>
                voiceActive ? stopVoice() : live.active ? void live.stop() : startVoice()
              }
              aria-pressed={anyVoiceActive}
            >
              <AudioLines className="h-4 w-4" /> {anyVoiceActive ? 'Voice on' : 'Voice mode'}
            </Button>
          )}
        </div>
      </header>

      {statusError && !status ? (
        <div
          role="alert"
          className="bg-card/60 flex flex-1 flex-col items-center justify-center gap-3 rounded-xl border p-6 text-center"
        >
          <Bot className="text-muted-foreground h-10 w-10" />
          <p className="font-medium">Flux Agent couldn’t be reached.</p>
          <p className="text-muted-foreground max-w-sm text-sm">{statusError}</p>
          <Button
            type="button"
            variant="outline"
            onClick={() => void loadAll()}
            className="gap-1.5"
          >
            <RefreshCw className="h-4 w-4" /> Try again
          </Button>
        </div>
      ) : !enabled ? (
        <div className="bg-card/60 flex flex-1 flex-col items-center justify-center gap-3 rounded-xl border p-6 text-center">
          <Bot className="text-muted-foreground h-10 w-10" />
          <p className="text-lg font-medium">Flux Agent isn’t configured yet</p>
          <p className="text-muted-foreground max-w-md text-sm">
            No AI model provider is connected for this workspace. Once an administrator configures
            one, you’ll be able to talk through your day here and let Flux Agent rebalance your
            schedule.
          </p>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 gap-4">
          <aside
            className={cn(
              'min-h-0 w-full shrink-0 md:block md:w-64',
              showList ? 'block' : 'hidden'
            )}
            aria-label="Conversations"
          >
            <FluxAgentConversationList
              conversations={conversations}
              activeId={activeId}
              loading={listLoading}
              trainer={!!status?.trainer}
              creating={creating}
              onSelect={id => void openConversation(id)}
              onCreate={mode => void createConversation(mode)}
              onDelete={conversation => void deleteConversation(conversation)}
            />
          </aside>

          <section
            className={cn(
              'bg-card/60 relative min-h-0 flex-1 flex-col overflow-hidden rounded-xl border shadow-sm',
              showList ? 'hidden md:flex' : 'flex'
            )}
            aria-label={activeConversation?.title || 'New conversation'}
            onDragOver={onDragOver}
            onDragLeave={event => {
              if (!event.currentTarget.contains(event.relatedTarget as Node | null))
                setDragging(false);
            }}
            onDrop={onDrop}
          >
            <div className="flex items-center gap-2 border-b px-4 py-2.5">
              <h2 className="min-w-0 flex-1 truncate text-sm font-medium">
                {activeConversation?.title || 'New conversation'}
              </h2>
              {activeConversation?.mode === 'trainer' && (
                <Badge variant="secondary" className="shrink-0 gap-1">
                  <GraduationCap className="h-3 w-3" /> Training mode
                </Badge>
              )}
              {canCoach && (
                <Button
                  type="button"
                  size="sm"
                  variant={coachOpen ? 'default' : 'outline'}
                  className="h-7 shrink-0 gap-1.5"
                  onClick={() => setCoachOpen(open => !open)}
                  aria-pressed={coachOpen}
                  aria-label="Coach this conversation"
                  title="Teach Flux Agent from this conversation"
                >
                  <GraduationCap className="h-3.5 w-3.5" /> Coach
                </Button>
              )}
            </div>

            <div
              className="native-scroll min-h-0 flex-1 overflow-y-auto px-3 py-4 sm:px-5"
              role="log"
              aria-label="Messages"
              aria-busy={threadLoading || !!pendingId}
            >
              {threadLoading && !messages.length ? (
                <p className="text-muted-foreground flex items-center gap-2 text-sm">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading conversation…
                </p>
              ) : threadError ? (
                <div role="alert" className="space-y-2 text-sm">
                  <p className="text-destructive">{threadError}</p>
                  {activeId && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => void openConversation(activeId)}
                    >
                      Retry
                    </Button>
                  )}
                </div>
              ) : messages.length === 0 && !pendingId ? (
                <div className="mx-auto flex max-w-md flex-col items-center gap-4 py-8 text-center">
                  <span className="bg-primary/15 text-primary flex h-12 w-12 items-center justify-center rounded-2xl">
                    {shownMode === 'trainer' ? (
                      <GraduationCap className="h-6 w-6" />
                    ) : (
                      <Sparkles className="h-6 w-6" />
                    )}
                  </span>
                  {shownMode === 'trainer' ? (
                    <p className="text-muted-foreground text-sm">
                      Training mode: coach Flux Agent on how it should respond. Attach training
                      files and it will update the shared playbooks as you go.
                    </p>
                  ) : (
                    <>
                      <p className="text-muted-foreground text-sm">
                        How’s your energy right now? Tell Flux Agent what’s going on — it will ask a
                        few questions and move things around so your plan fits how you actually
                        feel.
                      </p>
                      <div className="flex flex-col gap-2">
                        {STARTERS.map(starter => (
                          <button
                            key={starter}
                            type="button"
                            onClick={() => setDraft(starter)}
                            className="bg-background hover:bg-accent focus-visible:ring-ring rounded-lg border px-3 py-2 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2"
                          >
                            {starter}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <ol className="space-y-4">
                  {messages.map(message => (
                    <li
                      key={message.id}
                      className={cn(
                        'flex',
                        message.role === 'user' ? 'justify-end' : 'justify-start'
                      )}
                    >
                      {message.role === 'user' ? (
                        <div className="bg-primary text-primary-foreground max-w-[85%] rounded-2xl rounded-br-md px-3.5 py-2.5 text-sm sm:max-w-[75%]">
                          <span className="sr-only">You said: </span>
                          {message.content && (
                            <p className="whitespace-pre-wrap break-words">{message.content}</p>
                          )}
                          {message.files.some(file => file.kind === 'photo') && (
                            <ul className="mt-1.5 flex flex-wrap gap-1.5" aria-label="Photos">
                              {message.files
                                .filter(file => file.kind === 'photo')
                                .map((file, index) => (
                                  <li key={file.id} data-testid="flux-agent-message-photo">
                                    <FluxAgentPhotoThumb
                                      src={file.url}
                                      alt={`Photo ${index + 1} you sent`}
                                      className="h-24 w-24"
                                    />
                                  </li>
                                ))}
                            </ul>
                          )}
                          {message.files.some(file => file.kind !== 'photo') && (
                            <ul className="mt-1.5 flex flex-wrap gap-1" aria-label="Attachments">
                              {message.files.filter(file => file.kind !== 'photo').map(file => (
                                <li
                                  key={file.id}
                                  className="bg-primary-foreground/15 rounded-full px-2 py-0.5 text-xs"
                                >
                                  {file.name}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ) : (
                        <div className="flex max-w-[92%] gap-2 sm:max-w-[80%]">
                          <span
                            className="bg-primary/15 text-primary mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
                            aria-hidden="true"
                          >
                            <Sparkles className="h-3.5 w-3.5" />
                          </span>
                          <div className="bg-background min-w-0 rounded-2xl rounded-tl-md border px-3.5 py-2.5">
                            <span className="sr-only">Flux Agent said: </span>
                            <FluxAgentMarkdown content={message.content} />
                            <FluxAgentActionCards
                              actions={message.actions || []}
                              onSuggest={setDraft}
                              onResolved={card => {
                                const id = activeIdRef.current;
                                if (!id) return;
                                setThreads(current => ({
                                  ...current,
                                  [id]: withResolvedCard(current[id] || [], card),
                                }));
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </li>
                  ))}
                  {pendingId === activeId &&
                    pendingId &&
                    streaming?.conversationId === pendingId &&
                    (streaming.text ||
                      streaming.actions.length > 0 ||
                      streaming.forms.length > 0) && (
                      <li className="flex justify-start" data-testid="streaming-reply">
                        <div className="flex max-w-[92%] gap-2 sm:max-w-[80%]">
                          <span
                            className="bg-primary/15 text-primary mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
                            aria-hidden="true"
                          >
                            <Sparkles className="h-3.5 w-3.5" />
                          </span>
                          <div className="bg-background min-w-0 rounded-2xl rounded-tl-md border px-3.5 py-2.5">
                            <span className="sr-only">Flux Agent is replying: </span>
                            {streaming.text ? (
                              <FluxAgentMarkdown content={streaming.text} />
                            ) : (
                              <p className="text-muted-foreground text-sm">Working on it…</p>
                            )}
                            <FluxAgentActionCards
                              actions={streaming.actions}
                              onSuggest={setDraft}
                              liveForms={streaming.forms}
                            />
                          </div>
                        </div>
                      </li>
                    )}
                  {pendingId === activeId &&
                    pendingId &&
                    !streaming?.text &&
                    !streaming?.actions.length &&
                    !streaming?.forms.length && (
                      <li className="flex justify-start">
                        <div className="flex gap-2">
                          <span
                            className="bg-primary/15 text-primary mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
                            aria-hidden="true"
                          >
                            <Sparkles className="h-3.5 w-3.5" />
                          </span>
                          <div
                            className="bg-background text-muted-foreground rounded-2xl rounded-tl-md border px-3.5 py-2.5 text-sm"
                            role="status"
                          >
                            <span className="inline-flex items-center gap-1" aria-hidden="true">
                              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current [animation-delay:-0.3s]" />
                              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current [animation-delay:-0.15s]" />
                              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current" />
                            </span>
                            <span className="ml-2">
                              {pendingSeconds < 8
                                ? 'Thinking…'
                                : `Still working (${pendingSeconds}s) — it may be checking or adjusting your schedule.`}
                            </span>
                          </div>
                        </div>
                      </li>
                    )}
                </ol>
              )}
              <FluxAgentSuggestionCards
                conversationId={activeId}
                messages={messages}
                refreshKey={learningTick}
              />
              <div ref={threadEnd} />
            </div>

            {dragging && (
              <div className="border-primary bg-background/80 pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-xl border-2 border-dashed backdrop-blur-sm">
                <p className="flex items-center gap-2 text-sm font-medium">
                  <Upload className="h-4 w-4" /> Drop files to attach
                </p>
              </div>
            )}

            <div className="bg-background/60 space-y-2 border-t p-2.5 sm:p-3">
              {canVoice && !live.active && (
                <FluxAgentVoicePanel
                  voice={{ ...voice, start: startVoice, stop: stopVoice }}
                  ready={voiceReady}
                  onDismissReady={() => setVoiceReady(false)}
                />
              )}
              {liveMode && !voiceActive && !voiceReady && <FluxAgentLivePanel live={live} />}
              {sendError && (
                <div
                  role="alert"
                  className="border-destructive/40 bg-destructive/5 text-destructive flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm"
                >
                  <span>{sendError}</span>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7"
                    onClick={() => setSendError(null)}
                  >
                    Dismiss
                  </Button>
                </div>
              )}
              <FluxAgentComposer
                value={draft}
                onChange={setDraft}
                onSubmit={() => void submitDraft()}
                pending={!!pendingId}
                disabled={anyVoiceActive}
                maxChars={status?.limits.maxMessageChars ?? 0}
                maxAudioBytes={status?.limits.maxAudioBytes ?? 0}
                canTranscribe={!!status?.voice.transcribe}
                attachments={attachments.attachments}
                onAttach={attachments.addFiles}
                onRemoveAttachment={attachments.remove}
                uploading={attachments.uploading || photos.uploading}
                photos={photos.photos}
                onAddPhotos={photos.add}
                onRemovePhoto={photos.remove}
                maxPhotos={status?.photos?.maxPhotosPerMessage ?? 4}
              />
            </div>
          </section>
          {coachOpen && canCoach && activeConversation && (
            <FluxAgentCoachPanel
              key={activeConversation.id}
              conversationId={activeConversation.id}
              onClose={() => setCoachOpen(false)}
              onAccepted={rerunLast}
              className="fixed inset-2 z-40 md:static md:inset-auto md:z-auto md:w-[24rem] md:shrink-0 lg:w-[28rem]"
            />
          )}
        </div>
      )}

      {voiceDebug && <FluxAgentVoiceDebug voice={voice} />}
      <FluxAgentScheduled
        open={scheduledOpen}
        onOpenChange={scheduledVisibility}
        onConversation={id => void openConversation(id)}
      />
      <FluxViewsDialog open={viewsOpen} onOpenChange={setViewsOpen} onSuggest={setDraft} />
      <FluxAgentMemoryDialog open={memoryOpen} onOpenChange={setMemoryOpen} />
      {status?.trainer && (
        <FluxAgentTrainerConsole open={trainerOpen} onOpenChange={setTrainerOpen} />
      )}
    </div>
  );
}
